import React from 'react';

/**
 * Renders a list of selectable options (radio-style or multi-select) with
 * optional "Other" free-text inputs and "none" separator options.
 *
 * When `canEdit` is false the component renders in read-only mode: option
 * clicks are no-ops, textareas are disabled, and visual styling shifts to
 * indicate that interaction is not available.
 */
const SelectionOptions = ({ 
  question, 
  formState, 
  handleSelect, 
  handleOtherTextChange, 
  handleOtherTextBlur,
  canEdit = true,
  /** When true, highlight an empty required "other" textarea for the selected option. */
  highlightIncompleteOther = false,
}) => {
  return (
    <ul className="text-left space-y-2">
      {/* Main options */}
      {question.options.map((option) => {
        const currentSelections = formState[question.firestoreFieldName]?.value || [];
        const isSelected = currentSelections.includes(option);
        const hasOtherInput = question.otherOptions && option in question.otherOptions;
        const shouldShowOtherInput = hasOtherInput && isSelected;
        const otherValue = formState[question.firestoreFieldName]?.otherValue || '';
        const showOtherValueRequired =
          highlightIncompleteOther &&
          shouldShowOtherInput &&
          String(otherValue).trim().length === 0;

        return (
          <li
            key={option}
            onClick={() => canEdit && handleSelect(
              question.firestoreFieldName,
              option,
              isSelected,
              question,
              false
            )}
            className={`
              flex flex-col gap-3 p-2 
              rounded-lg transition-all duration-300 
              ${!canEdit
                ? 'cursor-not-allowed opacity-75'
                : 'cursor-pointer group'
              }
              ${isSelected
                ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                : canEdit
                  ? 'bg-violet-100 hover:bg-violet-200'
                  : 'bg-violet-100'
              }
            `}
          >
            <div className="flex items-center gap-3">
              <div className={`
                flex-shrink-0 w-4 h-4 rounded-full border-2
                transition-all duration-200 ease-in-out
                ${isSelected
                  ? 'border-violet-700 bg-violet-700'
                  : canEdit
                    ? 'border-gray-300 group-hover:border-violet-400'
                    : 'border-gray-300'
                }
                flex items-center justify-center
              `}>
                {isSelected && (
                  <div className="w-2 h-2 bg-white rounded transition-all duration-200 ease-in-out" />
                )}
              </div>
              <span className={`font-semibold transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                {option}
              </span>
            </div>

            {shouldShowOtherInput && (
              <>
                <textarea
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  placeholder={question.otherOptions[option] || "Please provide details"}
                  className={`font-medium w-full mt-1 p-2 border rounded-md text-black min-h-[60px] resize-none overflow-hidden ${
                    canEdit ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'
                  } ${showOtherValueRequired ? 'border-red-500 ring-1 ring-red-300' : ''}`}
                  value={otherValue}
                  onChange={(e) => canEdit && handleOtherTextChange(question.firestoreFieldName, e.target.value)}
                  onBlur={() => canEdit && handleOtherTextBlur(question.firestoreFieldName)}
                  onClick={(e) => e.stopPropagation()}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = (e.target.scrollHeight) + 'px';
                  }}
                  rows={2}
                  disabled={!canEdit}
                  aria-invalid={showOtherValueRequired || undefined}
                />
                {showOtherValueRequired && (
                  <p className="text-sm text-red-600 font-medium">
                    {question.otherOptions[option] || 'Please provide details'}
                  </p>
                )}
              </>
            )}
          </li>
        );
      })}

      {/* Separator and none options */}
      {question?.noneOptions && (
        <>
          <li className="text-center text-gray-700 my-3 font-medium">
            or
          </li>

          {question.noneOptions.map((noneOption) => {
            const currentSelections = formState[question.firestoreFieldName]?.value || [];
            const isSelected = currentSelections.includes(noneOption);
            const hasOtherInput = question.otherOptions && noneOption in question.otherOptions;
            const shouldShowOtherInput = hasOtherInput && isSelected;
            const otherValue = formState[question.firestoreFieldName]?.otherValue || '';
            const showOtherValueRequired =
              highlightIncompleteOther &&
              shouldShowOtherInput &&
              String(otherValue).trim().length === 0;

            return (
              <li
                key={noneOption}
                onClick={() => canEdit && handleSelect(
                  question.firestoreFieldName,
                  noneOption,
                  isSelected,
                  question,
                  true
                )}
                className={`
                  flex flex-col gap-3 p-2 
                  rounded-lg transition-all duration-300 
                  ${!canEdit
                    ? 'cursor-not-allowed opacity-75'
                    : 'cursor-pointer group'
                  }
                  ${isSelected
                    ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                    : canEdit
                      ? 'bg-violet-100 hover:bg-violet-200'
                      : 'bg-violet-100'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`
                    flex-shrink-0 w-4 h-4 rounded-full border-2
                    transition-all duration-200 ease-in-out
                    ${isSelected
                      ? 'border-violet-700 bg-violet-700'
                      : canEdit
                        ? 'border-gray-300 group-hover:border-violet-400'
                        : 'border-gray-300'
                    }
                    flex items-center justify-center
                  `}>
                    {isSelected && (
                      <div className="w-2 h-2 bg-white rounded transition-all duration-200 ease-in-out" />
                    )}
                  </div>
                  <span className={`font-semibold transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                    {noneOption}
                  </span>
                </div>

                {shouldShowOtherInput && (
                  <>
                    <textarea
                      ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      placeholder={question.otherOptions[noneOption] || "Please provide details"}
                      className={`font-medium w-full mt-1 p-2 border rounded-md text-black min-h-[60px] resize-none overflow-hidden ${
                        canEdit ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'
                      } ${showOtherValueRequired ? 'border-red-500 ring-1 ring-red-300' : ''}`}
                      value={otherValue}
                      onChange={(e) => canEdit && handleOtherTextChange(question.firestoreFieldName, e.target.value)}
                      onBlur={() => canEdit && handleOtherTextBlur(question.firestoreFieldName)}
                      onClick={(e) => e.stopPropagation()}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = (e.target.scrollHeight) + 'px';
                      }}
                      rows={2}
                      disabled={!canEdit}
                      aria-invalid={showOtherValueRequired || undefined}
                    />
                    {showOtherValueRequired && (
                      <p className="text-sm text-red-600 font-medium">
                        {question.otherOptions[noneOption] || 'Please provide details'}
                      </p>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </>
      )}
    </ul>
  );
};

export default SelectionOptions;
